import { Injectable, ChangeDetectorRef } from '@angular/core';
import { Subscription } from 'rxjs';
import {
  CoursePlayerService,
  CourseNavigationItem,
  CourseStructure,
  CourseEntitlementBlockCode,
  entitlementBlockCodeFrom,
  PlayerMode,
  PlayerConfig
} from '../../../../services/course-player.service';
import { CourseService } from '../../../../courses/course.service';
import { ProfileService } from '../../../../profile/profile.service';
import { Unit } from '../../../../instructor/units.service';
import { buildPreviewCourseShell } from '../pure-functions/preview-course-shell';
import { isLearnerCourseAccessDenial } from '../pure-functions/course-load-denial';

export interface ProgressLoadResult {
  courseProgressMap: Map<string, any>;
  isLoading: boolean;
  loadingError: string | null;
}

export interface CourseLoadResult {
  courseData: any;
  courseStarted: boolean;
  isReturningLearner: boolean;
  showLandingPage: boolean;
  playerConfig: PlayerConfig;
  playerMode?: PlayerMode;
  loadingError: string | null;
  isLoading: boolean;
  /**
   * KAN-415: the base course read (GET /courses/{id}) failed with a revoked-
   * access signal (403 COURSE_ACCESS_DENIED / NO_COURSE_ACCESS or 404
   * COURSE_NOT_FOUND) for the learner player — a departed/removed learner who
   * deep-linked an old URL. The modal renders a clean "no longer have access"
   * state instead of the retryable "Error Loading Course" crash.
   */
  accessDenied?: boolean;
}

@Injectable()
export class LearnerProgressService {
  private readonly DEBUG_MODE = false;

  constructor(
    private coursePlayerService: CoursePlayerService,
    private courseService: CourseService,
    private profileService: ProfileService
  ) {}

  loadCourse(
    courseId: string,
    playerMode: PlayerMode,
    playerConfig: PlayerConfig,
    subscriptions: Subscription[],
    onCourseLoaded: (result: CourseLoadResult) => void,
    onStructureLoaded: () => void,
    previewUnit?: Unit
  ): void {
    // Preview-with-no-courseId branch: build a minimal course shell from the
    // provided Unit and skip the backend fetch entirely. This unblocks
    // previewing a unit from the Content Hub library.
    if (!courseId && playerMode === 'instructor_preview' && previewUnit) {
      const courseShell = buildPreviewCourseShell(previewUnit);
      onCourseLoaded({
        courseData: courseShell,
        courseStarted: true,
        isReturningLearner: false,
        showLandingPage: false,
        playerConfig,
        loadingError: null,
        isLoading: true
      });
      onStructureLoaded();
      return;
    }

    if (!courseId) {
      // Learner mode (or any non-preview mode) requires a courseId.
      console.error('[LearnerModal] loadCourse called without courseId and no previewUnit in preview mode');
      onCourseLoaded({
        courseData: null,
        courseStarted: false,
        isReturningLearner: false,
        showLandingPage: true,
        playerConfig,
        loadingError: 'Cannot load course: no course identifier provided.',
        isLoading: false
      });
      return;
    }

    const courseSub = this.courseService.getCourse(courseId).subscribe({
      next: (course) => {
        console.log('[LearnerModal] Course loaded:', course);

        let updatedConfig = playerConfig;

        // Auto-detect instructor preview: non-enrolled instructor viewing a course
        if (playerMode === 'learner' && !(course as any).computedStatus) {
          const currentUser = this.profileService.getCurrentUser();
          const role = currentUser?.currentOrgRole;
          if (role && ['admin', 'instructor', 'senior_instructor'].includes(role)) {
            playerMode = 'instructor_preview';
            updatedConfig = this.coursePlayerService.getPlayerConfig('instructor_preview');
          }
        }

        // KAN-332 QA fix: gate on the backend-resolved EFFECTIVE per-learner
        // navigation mode (course default overridden by this learner's
        // course_access.navigation_mode, if set) instead of the raw
        // course.freeNavigation column. Falls back to the raw flag when the
        // effective field is absent (e.g. an older cached response or the
        // locally-built preview shell), preserving prior behavior there.
        const effectiveFreeNavigation = (course as any).effectiveFreeNavigation ?? (course as any).freeNavigation;
        if (playerMode === 'learner' || playerMode === 'instructor_preview') {
          updatedConfig = {
            ...updatedConfig,
            enforceSequential: !effectiveFreeNavigation,
            allowJumpNavigation: !!effectiveFreeNavigation
          };
          console.log('[LearnerModal] Effective navigation mode applied:', {
            effectiveFreeNavigation,
            enforceSequential: updatedConfig.enforceSequential
          });
        }

        let courseStarted: boolean;
        let isReturningLearner: boolean;
        let showLandingPage: boolean;

        if (playerMode !== 'learner') {
          courseStarted = true;
          showLandingPage = false;
          isReturningLearner = false;
        } else {
          isReturningLearner = !!course.startedAt;
          courseStarted = !!course.startedAt;
          showLandingPage = true;
        }

        onCourseLoaded({
          courseData: course,
          courseStarted,
          isReturningLearner,
          showLandingPage,
          playerConfig: updatedConfig,
          playerMode,
          loadingError: null,
          isLoading: true
        });

        onStructureLoaded();
      },
      error: (error) => {
        console.error('[LearnerModal] Failed to load course:', error);
        // KAN-415: a departed/removed learner who deep-links an old course URL
        // hits the base read first; it 403s (COURSE_ACCESS_DENIED) or 404s
        // (COURSE_NOT_FOUND). Surface a clean access-denied state (handled by
        // the modal) rather than the retryable crash — a retry can't restore
        // revoked access.
        if (isLearnerCourseAccessDenial(error, playerMode)) {
          onCourseLoaded({
            courseData: null,
            courseStarted: false,
            isReturningLearner: false,
            showLandingPage: true,
            playerConfig,
            loadingError: null,
            isLoading: false,
            accessDenied: true
          });
          return;
        }
        const isAccessDenied = error?.status === 403;
        onCourseLoaded({
          courseData: null,
          courseStarted: false,
          isReturningLearner: false,
          showLandingPage: true,
          playerConfig,
          loadingError: isAccessDenied
            ? 'You do not have access to this course.'
            : 'Failed to load course. Please try again.',
          isLoading: false
        });
      }
    });

    subscriptions.push(courseSub);
  }

  loadCourseStructure(
    courseId: string,
    subscriptions: Subscription[],
    onStructureLoaded: (structure: CourseStructure) => void,
    onError: (error: string, entitlementCode?: CourseEntitlementBlockCode | null) => void,
    previewUnit?: Unit
  ): void {
    // Preview-with-unit: synthesize the structure locally, no backend round trip.
    if (previewUnit) {
      try {
        const structure = this.coursePlayerService.buildStructureFromUnit(previewUnit);
        console.log('[LearnerModal] Structure built from previewUnit:', structure);
        onStructureLoaded(structure);
      } catch (error) {
        console.error('[LearnerModal] Failed to build preview structure:', error);
        onError('Failed to load preview content. Please try again.');
      }
      return;
    }

    const structureSub = this.coursePlayerService.getCourseStructure(courseId).subscribe({
      next: (structure) => {
        console.log('[LearnerModal] Structure loaded:', structure);
        onStructureLoaded(structure);
      },
      error: (error) => {
        console.error('[LearnerModal] Failed to load structure:', error);
        // KAN-353: the task-5.3 entitlement 403 gets code-specific copy and
        // carries its code so the modal can render the Buy/Renew CTA.
        const entitlementCode = entitlementBlockCodeFrom(error);
        if (entitlementCode === 'COURSE_ACCESS_EXPIRED') {
          onError(
            'Your access to this course has expired. Your progress is saved - renew access to continue where you left off.',
            entitlementCode
          );
          return;
        }
        if (entitlementCode === 'COURSE_ACCESS_REQUIRED') {
          onError('This course requires purchase before you can view its content.', entitlementCode);
          return;
        }
        onError('Failed to load course content. Please try again.', null);
      }
    });

    subscriptions.push(structureSub);
  }

  loadCourseProgress(
    courseId: string,
    playerMode: PlayerMode,
    courseStructure: CourseStructure | null,
    courseProgressMap: Map<string, any>,
    tempInstructorProgress: Map<string, any>,
    subscriptions: Subscription[],
    setupInitial: boolean,
    onComplete: (courseProgressMap: Map<string, any>) => void,
    onError: () => void
  ): void {
    if (playerMode === 'instructor_preview' || playerMode === 'instructor_test') {
      console.log('[LearnerModal] Instructor mode - using temporary progress');
      courseProgressMap.clear();
      tempInstructorProgress.forEach((value, key) => {
        courseProgressMap.set(key, value);
      });
      onComplete(courseProgressMap);
      return;
    }

    const progressSub = this.courseService.getCourseProgress(courseId).subscribe({
      next: (progressArray) => {
        console.log('[LearnerModal] Progress loaded:', progressArray);
        courseProgressMap.clear();
        progressArray.forEach(progress => {
          courseProgressMap.set(progress.moduleId, progress);
        });

        // Initialize hasSubmission field for quiz navigation items
        courseStructure?.navigationItems.forEach(item => {
          if (item.hasQuiz || item.type === 'unit_quiz') {
            try {
              const progressKey = this.coursePlayerService.getProgressKey(item);
              if (!progressKey) {
                console.error('[LearnerModal] PHASE 1 - Invalid progressKey for item:', item.id);
                return;
              }
              const progress = courseProgressMap.get(progressKey);
              if (progress && typeof progress !== 'object') {
                console.error('[LearnerModal] PHASE 1 - Invalid progress data type:', typeof progress);
                return;
              }
              const hasQuizSubmission = progress?.quizScore !== null && progress?.quizScore !== undefined;
              item.hasSubmission = hasQuizSubmission;
              if (this.DEBUG_MODE) {
                console.log('[LearnerModal] PHASE 1 - Initialized hasSubmission:', {
                  itemId: item.id,
                  hasSubmission: hasQuizSubmission,
                  quizScore: progress?.quizScore,
                  status: item.status
                });
              }
            } catch (error) {
              console.error('[LearnerModal] PHASE 1 - Error processing item:', item.id, error);
            }
          }
        });

        // Populate unit quiz completion data
        this.coursePlayerService.populateUnitQuizCompletion(
          courseStructure!.navigationItems,
          courseProgressMap
        );

        onComplete(courseProgressMap);
      },
      error: (error) => {
        console.error('[LearnerModal] Failed to load progress:', error);
        onError();
      }
    });

    subscriptions.push(progressSub);
  }

  /**
   * Convenience: re-syncs item statuses from progress AND inserts the
   * synthetic `course_summary` nav item if every playable item is now
   * complete. The learner modal used to call these two service methods
   * back-to-back; this wrapper keeps the parent component lean.
   */
  syncProgressAndSummary(
    courseStructure: CourseStructure | null,
    courseProgressMap: Map<string, any>,
    cdr: ChangeDetectorRef,
    timelineComponent: any,
    updateNavigationState: () => void,
    suppressSummary: boolean = false,
    enforceSequential: boolean = true
  ): void {
    this.updateNavigationStatusesFromProgress(
      courseStructure,
      courseProgressMap,
      cdr,
      timelineComponent,
      enforceSequential
    );
    if (suppressSummary) return;
    this.addCourseSummaryIfNeeded(
      courseStructure,
      courseProgressMap,
      cdr,
      updateNavigationState
    );
  }

  /**
   * KAN-332 QA round 2 (Fix 1, lock-icon half): re-derive each item's status
   * from predecessor completion instead of blindly flipping any non-complete
   * item to 'available' the moment a progress row exists for it. The old
   * blind-overwrite hid the timeline's lock icon (and made the item look
   * clickable) for any module whose lock came from the backend lock map
   * (free_navigation / a per-learner navigation_mode override), the instant
   * a progress row existed for it - which is common (started-but-incomplete
   * rows, or eager placeholder rows created at enrollment).
   *
   * `enforceSequential` should be the CALLER's current PlayerConfig value
   * (itself now resolved from the backend's effective per-learner navigation
   * mode - see LearnerProgressService.loadCourse) so a 'locked' override
   * correctly re-locks items here even when the raw course default is open,
   * and an 'open' override correctly leaves everything unlocked.
   */
  updateNavigationStatusesFromProgress(
    courseStructure: CourseStructure | null,
    courseProgressMap: Map<string, any>,
    cdr: ChangeDetectorRef,
    timelineComponent: any,
    enforceSequential: boolean = true
  ): void {
    if (!courseStructure) return;

    const items = courseStructure.navigationItems;

    items.forEach(item => {
      if (!item.isPlayable) return;
      const progressKey = this.coursePlayerService.getProgressKey(item);
      const progress = courseProgressMap.get(progressKey);
      const isComplete = !!(progress && (progress.moduleCompleted || progress.completed));

      if (isComplete) {
        item.status = 'completed';
      } else if (this.isLockedBySequentialGate(item, items, courseProgressMap, enforceSequential)) {
        item.status = 'locked';
      } else {
        item.status = 'available';
      }
    });

    cdr.detectChanges();

    if (timelineComponent) {
      timelineComponent.calculateProgress();
    }
  }

  /**
   * Mirrors LearnerNavigationService.canAccessItem's predecessor walk (the
   * canonical client-side sequential gate) so the timeline's status/lock-icon
   * agrees with what actually blocks Next/jump navigation. Duplicated here
   * (rather than injecting LearnerNavigationService) to keep this service's
   * dependency graph unchanged; the two must be kept in sync if either changes.
   */
  private isLockedBySequentialGate(
    item: CourseNavigationItem,
    items: CourseNavigationItem[],
    courseProgressMap: Map<string, any>,
    enforceSequential: boolean
  ): boolean {
    if (!enforceSequential) return false;
    if (item.type === 'unit_intro') return false;

    const itemIndex = items.indexOf(item);
    for (let i = 0; i < itemIndex; i++) {
      const prevItem = items[i];
      if (!prevItem || prevItem.type === 'unit_intro') continue;

      const progressKey = this.coursePlayerService.getProgressKey(prevItem);
      const progress = courseProgressMap.get(progressKey);
      if (!progress || (!progress.moduleCompleted && !progress.completed)) {
        return true;
      }
    }
    return false;
  }

  areAllModulesComplete(
    courseStructure: CourseStructure | null,
    courseProgressMap: Map<string, any>
  ): boolean {
    if (!courseStructure) return false;

    const playableItems = courseStructure.navigationItems.filter(
      item => item.isPlayable && item.type !== 'course_summary'
    );

    if (playableItems.length === 0) return false;

    return playableItems.every(item => {
      const progressKey = this.coursePlayerService.getProgressKey(item);
      const progress = courseProgressMap.get(progressKey);
      return progress?.moduleCompleted || progress?.completed || false;
    });
  }

  addCourseSummaryIfNeeded(
    courseStructure: CourseStructure | null,
    courseProgressMap: Map<string, any>,
    cdr: ChangeDetectorRef,
    updateNavigationState: () => void
  ): void {
    if (!courseStructure) return;

    const allComplete = this.areAllModulesComplete(courseStructure, courseProgressMap);
    const hasSummary = courseStructure.navigationItems.some(item => item.type === 'course_summary');

    if (allComplete && !hasSummary) {
      console.log('[LearnerModal] Adding course summary page to navigation');

      const summaryItem: CourseNavigationItem = {
        id: 'course-summary',
        name: 'Course Summary',
        type: 'course_summary',
        status: 'available',
        isDirectModule: true,
        globalIndex: courseStructure.navigationItems.length,
        isPlayable: true
      };

      courseStructure.navigationItems.push(summaryItem);
      updateNavigationState();
      cdr.detectChanges();
    }
  }

  onModuleComplete(courseId: string, reloadProgress: () => void): void {
    console.log('[LearnerModal] Module complete. User can navigate to next item manually.');
    reloadProgress();
  }

  /**
   * KAN-367: human-readable reason the course could not be finished when the
   * learner clicked "Complete" on the last item and no summary appeared. Powers
   * the safety-net notification so the action is never a silent no-op. Kept
   * simple and always-accurate: the only requirement that can strand a
   * passed-quiz module is an unmet video-watch requirement.
   *
   * KAN-597: `finishingCourse` is false for a mid-course module, where the
   * learner is trying to move on, not to finish the course.
   */
  describeIncompleteRequirement(item: CourseNavigationItem | null, finishingCourse = true): string {
    const requiresVideo = !!item?.hasVideo && (item?.requiredWatchPercentage ?? 90) > 0;
    if (requiresVideo) {
      return finishingCourse
        ? 'You still need to watch the required video for this module before the course can be completed.'
        : 'You still need to watch the required video for this module before you can move on.';
    }
    return finishingCourse
      ? 'This module still has unfinished requirements, so the course cannot be completed yet. Please review the module and try again.'
      : 'This module still has unfinished requirements, so you cannot move on yet. Please review the module and try again.';
  }

  /**
   * KAN-198: Reactively reflect backend module completion in the sidebar/timeline.
   *
   * The backend returns `moduleCompleted: true` from updateVideoProgress when the
   * watch requirement is met (DB trigger), and `VideoProgressService.moduleCompleted$`
   * emits the moduleId. Without this hook the local `courseProgressMap` stays stale
   * until the page is refreshed, so the sidebar checkmark and overall progress
   * percent never update reactively for video-only modules.
   *
   * Returns `true` if local state changed (caller should refresh nav statuses).
   */
  applyBackendModuleCompletion(
    moduleId: string,
    courseStructure: CourseStructure | null,
    courseProgressMap: Map<string, any>,
    cdr: ChangeDetectorRef,
    timelineComponent: any,
    updateNavigationState: () => void,
    suppressSummary: boolean = false,
    enforceSequential: boolean = true
  ): boolean {
    if (!moduleId || !courseStructure) return false;

    const item = courseStructure.navigationItems.find(i => i.id === moduleId);
    if (!item) return false;
    if (!item.isPlayable) return false;

    const progressKey = this.coursePlayerService.getProgressKey(item) || moduleId;
    const existing = courseProgressMap.get(progressKey) || {};

    if (existing.moduleCompleted === true && existing.videoCompleted === true) {
      return false;
    }

    courseProgressMap.set(progressKey, {
      ...existing,
      moduleId: existing.moduleId || moduleId,
      moduleCompleted: true,
      videoCompleted: true
    });

    this.syncProgressAndSummary(
      courseStructure,
      courseProgressMap,
      cdr,
      timelineComponent,
      updateNavigationState,
      suppressSummary,
      enforceSequential
    );

    return true;
  }

  onProgressUpdate(
    event: any,
    courseProgressMap: Map<string, any>,
    courseStructure: CourseStructure | null,
    cdr: ChangeDetectorRef,
    updateNavigationState: () => void,
    suppressSummary: boolean = false
  ): void {
    if (event.moduleId) {
      const existingProgress = courseProgressMap.get(event.moduleId) || {};
      courseProgressMap.set(event.moduleId, {
        ...existingProgress,
        ...event
      });
    }
    if (suppressSummary) return;
    this.addCourseSummaryIfNeeded(courseStructure, courseProgressMap, cdr, updateNavigationState);
  }
}
